import { useEffect, useMemo, useState } from "react"

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"

import MainLayout from "../../layouts/MainLayout"
import api from "../../services/api"

import {
  controlPurchaseReceipt,
  createPurchaseReceipt,
  getPurchaseOrderReceiptItems,
  getPurchaseReceiptFormData,
  getPurchaseReceipts,
} from "../../services/purchaseReceiptService"


// =========================================================
// Helpers
// =========================================================


const nowLocal = () => {
  const date = new Date()

  date.setMinutes(
    date.getMinutes() - date.getTimezoneOffset()
  )

  return date
    .toISOString()
    .slice(0, 16)
}


const createDefaultFormData = () => ({
  purchase_order_id: "",
  supplier_delivery_note: "",
  receipt_date: nowLocal(),
  notes: "",
  items: [],
})


const getArray = (data, ...keys) => {
  for (const key of keys) {
    if (Array.isArray(data?.[key])) {
      return data[key]
    }
  }

  return []
}


const numberValue = (value) => {
  const number = Number(value)

  return Number.isFinite(number)
    ? number
    : 0
}


const formatQuantity = (value) => {
  const number = numberValue(value)

  return new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 3,
  }).format(number)
}


const getUnitLabel = (unit) => {
  const value = String(unit || "")
    .trim()
    .toLowerCase()

  const labels = {
    piece: "pièce",
    pieces: "pièces",

    kg: "kg",
    kilogram: "kg",
    kilogramme: "kg",

    g: "g",
    gram: "g",
    gramme: "g",

    l: "L",
    litre: "L",
    liter: "L",

    ml: "mL",

    m: "m",
    metre: "m",
    meter: "m",

    m2: "m²",
    m3: "m³",

    carton: "carton",
    sac: "sac",
    rouleau: "rouleau",
  }

  return labels[value] || unit || "unité"
}


const statusLabel = (status) => {
  const normalized = String(status || "")
    .toUpperCase()

  const labels = {
    RECEIVED: "Reçue",
    CONTROLLED: "Contrôlée",
    VALIDATED: "Validée",
    POSTED: "Mise en stock",
  }

  return labels[normalized] || status || "-"
}


const statusColor = (status) => {
  const normalized = String(status || "")
    .toUpperCase()

  if (
    ["CONTROLLED", "VALIDATED", "POSTED"].includes(
      normalized
    )
  ) {
    return "success"
  }

  if (normalized === "RECEIVED") {
    return "warning"
  }

  return "default"
}


const postReceiptToStock = async (receiptId) => {
  const response = await api.post(
    `/purchase-receipts/${receiptId}/post-stock`
  )
  return response.data
}


// =========================================================
// Page
// =========================================================



function MetricCard({
  label,
  value,
  caption,
  accent = "#2563eb",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        minWidth: 0,
        minHeight: 100,
        borderRadius: 3.5,
        borderLeft: "4px solid",
        borderLeftColor: accent,
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          fontWeight: 800,
          letterSpacing: 0.25,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          mt: 0.35,
          fontSize: { xs: 24, md: 28 },
          lineHeight: 1.15,
          fontWeight: 900,
          color: "#0f172a",
        }}
      >
        {value}
      </Typography>

      {caption ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.6 }}
        >
          {caption}
        </Typography>
      ) : null}
    </Paper>
  )
}

function PurchaseReceiptsPage() {
  const [receipts, setReceipts] = useState([])
  const [orders, setOrders] = useState([])

  const [loading, setLoading] = useState(false)
  const [loadingItems, setLoadingItems] =
    useState(false)

  const [saving, setSaving] = useState(false)
  const [postingReceiptId, setPostingReceiptId] = useState(null)

  const [open, setOpen] = useState(false)

  const [formData, setFormData] = useState(
    createDefaultFormData()
  )

  const [selectedOrder, setSelectedOrder] =
    useState(null)

  const [searchTerm, setSearchTerm] =
    useState("")

  const [page, setPage] = useState(0)
  const rowsPerPage = 5

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  // Pagination reset
  useEffect(() => {
    setPage(0)
  }, [searchTerm])

  // =======================================================
  // Snackbar
  // =======================================================


  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message,
    })
  }


  // =======================================================
  // Load
  // =======================================================


  const loadData = async () => {
    try {
      setLoading(true)

      const [receiptList, form] =
        await Promise.all([
          getPurchaseReceipts(),
          getPurchaseReceiptFormData(),
        ])

      setReceipts(
        Array.isArray(receiptList)
          ? receiptList
          : []
      )

      setOrders(
        getArray(
          form,
          "purchase_orders",
          "receivable_purchase_orders",
          "orders"
        )
      )
    } catch (error) {
      console.error(error)

      showSnackbar(
        error?.response?.data?.detail ||
          "Impossible de charger les réceptions fournisseurs.",
        "error"
      )
    } finally {
      setLoading(false)
    }
  }


  useEffect(() => {
    loadData()
  }, [])


  // =======================================================
  // Dialog
  // =======================================================


  const handleOpen = () => {
    setSelectedOrder(null)

    setFormData(
      createDefaultFormData()
    )

    setOpen(true)
  }


  const handleClose = () => {
    if (saving) return

    setOpen(false)
    setSelectedOrder(null)

    setFormData(
      createDefaultFormData()
    )
  }


  // =======================================================
  // PO selection
  // =======================================================


  const handleOrderChange = async (orderId) => {
    if (!orderId) {
      setSelectedOrder(null)

      setFormData((prev) => ({
        ...prev,
        purchase_order_id: "",
        items: [],
      }))

      return
    }

    try {
      setLoadingItems(true)

      setFormData((prev) => ({
        ...prev,
        purchase_order_id: Number(orderId),
        items: [],
      }))

      const order =
        await getPurchaseOrderReceiptItems(
          Number(orderId)
        )

      setSelectedOrder(order || null)

      const sourceItems = Array.isArray(
        order?.items
      )
        ? order.items
        : []

      const mappedItems = sourceItems.map(
        (item) => {
          const remaining = numberValue(
            item.remaining_quantity
          )

          return {
            purchase_order_item_id:
              Number(item.id),

            product_id:
              Number(item.product_id),

            product_code:
              item.product_code || "",

            product_name:
              item.product_name ||
              `Produit #${item.product_id}`,

            unit:
              item.unit || "piece",

            ordered_quantity:
              numberValue(
                item.ordered_quantity
              ),

            already_received_quantity:
              numberValue(
                item.received_quantity
              ),

            remaining_quantity:
              remaining,

            delivered_quantity:
              remaining,

            accepted_quantity:
              remaining,

            rejected_quantity:
              0,

            rejection_reason: "",
            notes: "",
          }
        }
      )

      setFormData((prev) => ({
        ...prev,
        purchase_order_id:
          Number(orderId),

        items: mappedItems,
      }))

      if (!mappedItems.length) {
        showSnackbar(
          "Ce bon de commande ne contient plus aucun article à réceptionner.",
          "warning"
        )
      }
    } catch (error) {
      console.error(error)

      setSelectedOrder(null)

      setFormData((prev) => ({
        ...prev,
        items: [],
      }))

      showSnackbar(
        error?.response?.data?.detail ||
          "Impossible de charger les articles du bon de commande.",
        "error"
      )
    } finally {
      setLoadingItems(false)
    }
  }


  // =======================================================
  // Items
  // =======================================================


  const handleItemChange = (
    index,
    field,
    value
  ) => {
    setFormData((prev) => ({
      ...prev,

      items: prev.items.map(
        (item, currentIndex) => {
          if (currentIndex !== index) {
            return item
          }

          const updated = {
            ...item,
            [field]: [
              "delivered_quantity",
              "accepted_quantity",
              "rejected_quantity",
            ].includes(field)
              ? numberValue(value)
              : value,
          }

          return updated
        }
      ),
    }))
  }


  const handleDeliveredChange = (
    index,
    value
  ) => {
    const delivered = Math.max(
      0,
      numberValue(value)
    )

    setFormData((prev) => ({
      ...prev,

      items: prev.items.map(
        (item, currentIndex) => {
          if (currentIndex !== index) {
            return item
          }

          return {
            ...item,

            delivered_quantity:
              delivered,

            // Par défaut, on considère tout
            // comme accepté.
            accepted_quantity:
              Math.min(
                delivered,
                numberValue(
                  item.remaining_quantity
                )
              ),

            rejected_quantity:
              Math.max(
                0,
                delivered -
                  Math.min(
                    delivered,
                    numberValue(
                      item.remaining_quantity
                    )
                  )
              ),
          }
        }
      ),
    }))
  }


  // =======================================================
  // Save
  // =======================================================


  const handleSave = async () => {
    if (!formData.purchase_order_id) {
      showSnackbar(
        "Sélectionnez un bon de commande.",
        "warning"
      )

      return
    }

    if (!formData.items.length) {
      showSnackbar(
        "Aucun article n'est disponible pour cette réception.",
        "warning"
      )

      return
    }

    const hasDelivery =
      formData.items.some(
        (item) =>
          numberValue(
            item.delivered_quantity
          ) > 0
      )

    if (!hasDelivery) {
      showSnackbar(
        "Saisissez au moins une quantité livrée.",
        "warning"
      )

      return
    }

    for (const item of formData.items) {
      const delivered =
        numberValue(
          item.delivered_quantity
        )

      const accepted =
        numberValue(
          item.accepted_quantity
        )

      const rejected =
        numberValue(
          item.rejected_quantity
        )

      const remaining =
        numberValue(
          item.remaining_quantity
        )

      if (
        delivered < 0 ||
        accepted < 0 ||
        rejected < 0
      ) {
        showSnackbar(
          `Les quantités de ${item.product_name} ne peuvent pas être négatives.`,
          "warning"
        )

        return
      }

      if (
        Math.abs(
          accepted +
            rejected -
            delivered
        ) > 0.0001
      ) {
        showSnackbar(
          `${item.product_name} : accepté + rejeté doit être égal à livré.`,
          "warning"
        )

        return
      }

      if (accepted > remaining) {
        showSnackbar(
          `${item.product_name} : la quantité acceptée ne peut pas dépasser le restant de ${formatQuantity(
            remaining
          )} ${getUnitLabel(item.unit)}.`,
          "warning"
        )

        return
      }

      if (
        rejected > 0 &&
        !item.rejection_reason?.trim()
      ) {
        showSnackbar(
          `Indiquez le motif du rejet pour ${item.product_name}.`,
          "warning"
        )

        return
      }
    }

    const payload = {
      purchase_order_id:
        Number(
          formData.purchase_order_id
        ),

      supplier_delivery_note:
        formData
          .supplier_delivery_note
          ?.trim() || null,

      receipt_date:
        formData.receipt_date
          ? new Date(
              formData.receipt_date
            ).toISOString()
          : null,

      notes:
        formData.notes?.trim() ||
        null,

      items: formData.items
        .filter(
          (item) =>
            numberValue(
              item.delivered_quantity
            ) > 0
        )
        .map((item) => ({
          purchase_order_item_id:
            Number(
              item.purchase_order_item_id
            ),

          delivered_quantity:
            numberValue(
              item.delivered_quantity
            ),

          accepted_quantity:
            numberValue(
              item.accepted_quantity
            ),

          rejected_quantity:
            numberValue(
              item.rejected_quantity
            ),

          rejection_reason:
            item.rejection_reason
              ?.trim() || null,

          notes:
            item.notes?.trim() ||
            null,
        })),
    }

    try {
      setSaving(true)

      const response =
        await createPurchaseReceipt(
          payload
        )

      showSnackbar(
        response?.code
          ? `Réception ${response.code} enregistrée avec succès.`
          : "Réception fournisseur enregistrée avec succès."
      )

      handleClose()

      await loadData()
    } catch (error) {
      console.error(error)

      showSnackbar(
        error?.response?.data?.detail ||
          "Impossible d'enregistrer la réception.",
        "error"
      )
    } finally {
      setSaving(false)
    }
  }


  // =======================================================
  // Control
  // =======================================================


  const handleControl = async (
    receiptId
  ) => {
    const confirmed =
      window.confirm(
        [
          "Confirmer le contrôle de cette réception ?",
          "",
          "Les quantités acceptées seront alors prêtes pour la mise en stock.",
        ].join("\n")
      )

    if (!confirmed) {
      return
    }

    try {
      await controlPurchaseReceipt(
        receiptId,
        {}
      )

      showSnackbar(
        "Réception contrôlée avec succès."
      )

      await loadData()
    } catch (error) {
      console.error(error)

      showSnackbar(
        error?.response?.data?.detail ||
          "Impossible de contrôler cette réception.",
        "error"
      )
    }
  }


  // =======================================================
  // Mise en stock
  // =======================================================

  const handlePostStock = async (receipt) => {
    const receiptCode =
      receipt?.code ||
      receipt?.receipt_number ||
      `RCV-${receipt?.id}`

    const warehouseLabel =
      [receipt?.warehouse_code, receipt?.warehouse_name]
        .filter(Boolean)
        .join(" - ") || "l'entrepôt"

    const confirmed = window.confirm(
      [
        `Mettre ${receiptCode} en stock ?`,
        "",
        `Les quantités acceptées seront ajoutées dans ${warehouseLabel}.`,
        "Cette opération est protégée contre une double mise en stock.",
      ].join("\n")
    )

    if (!confirmed) return

    try {
      setPostingReceiptId(receipt.id)
      const response = await postReceiptToStock(receipt.id)

      showSnackbar(
        response?.message ||
          "Réception mise en stock avec succès."
      )

      await loadData()
    } catch (error) {
      console.error(error)

      showSnackbar(
        error?.response?.data?.detail ||
          "Impossible de mettre cette réception en stock.",
        "error"
      )
    } finally {
      setPostingReceiptId(null)
    }
  }


  // =======================================================
  // Filter
  // =======================================================


  const filtered = useMemo(() => {
    const q = searchTerm
      .toLowerCase()
      .trim()

    if (!q) {
      return receipts
    }

    return receipts.filter(
      (receipt) => {
        const haystack = [
          receipt.code,
          receipt.receipt_number,

          receipt.purchase_order_code,

          receipt.supplier_code,
          receipt.supplier_name,

          receipt.supplier_delivery_note,

          receipt.warehouse_code,
          receipt.warehouse_name,

          receipt.status,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()

        return haystack.includes(q)
      }
    )
  }, [receipts, searchTerm])


  // =======================================================
  // Render
  // =======================================================


  return (
    <MainLayout>
      <Box
        sx={{
          minWidth: 0,
          pb: { xs: 2, md: 4 },
          "& .MuiPaper-root": {
            borderColor: "rgba(148, 163, 184, 0.24)",
            backgroundImage: "none",
          },
          "& .MuiButton-root": {
            textTransform: "none",
            fontWeight: 800,
            borderRadius: 2.5,
          },
          "& .MuiTextField-root .MuiOutlinedInput-root": {
            borderRadius: 2.5,
          },
          "& .MuiAlert-root": {
            borderRadius: 3,
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            backgroundColor: "#f8fafc",
            color: "#334155",
            fontWeight: 800,
            whiteSpace: "nowrap",
          },
          "& .MuiTableBody-root .MuiTableRow-root:hover": {
            backgroundColor: "#f8fbff",
          },
          "& .MuiChip-root": {
            fontWeight: 700,
          },
        }}
      >
        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 1fr) auto",
            },
            alignItems: "start",
            columnGap: 2,
            rowGap: 1.25,
            mb: 2.5,
            width: "100%",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{
                color: "#0f172a",
                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.15rem",
                },
                lineHeight: 1.12,
                letterSpacing: "-0.02em",
              }}
            >
              Réceptions fournisseurs
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                maxWidth: 760,
                lineHeight: 1.55,
              }}
            >
              Réception physique, contrôle des quantités et préparation de la mise en stock.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: {
                xs: "stretch",
                md: "flex-end",
              },
              justifySelf: {
                xs: "stretch",
                md: "end",
              },
              width: {
                xs: "100%",
                md: "auto",
              },
              pt: {
                xs: 0.5,
                md: 0.25,
              },
            }}
          >
            <Button
              variant="contained"
              onClick={handleOpen}
              sx={{
                width: {
                  xs: "100%",
                  sm: "auto",
                },
                minHeight: 42,
                px: 2.25,
                borderRadius: 2.5,
                whiteSpace: "nowrap",
                boxShadow: "0 8px 20px rgba(37, 99, 235, 0.18)",
              }}
            >
              Nouvelle réception
            </Button>
          </Box>
        </Box>


        {/* =================================================
            INFO WORKFLOW
        ================================================= */}


        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(4, minmax(0, 1fr))",
            },
            gap: 1.5,
            mb: 2,
          }}
        >
          <MetricCard
            label="Réceptions"
            value={receipts.length}
            caption="Total enregistré"
          />
          <MetricCard
            label="À contrôler"
            value={
              receipts.filter(
                (receipt) =>
                  String(receipt.status || "").toUpperCase() ===
                  "RECEIVED"
              ).length
            }
            caption="Contrôle qualité attendu"
            accent="#f59e0b"
          />
          <MetricCard
            label="Prêtes pour stock"
            value={
              receipts.filter(
                (receipt) =>
                  String(receipt.status || "").toUpperCase() ===
                    "CONTROLLED" && !receipt.stock_posted
              ).length
            }
            caption="Quantités contrôlées"
            accent="#0891b2"
          />
          <MetricCard
            label="Mises en stock"
            value={
              receipts.filter(
                (receipt) =>
                  Boolean(receipt.stock_posted) ||
                  String(receipt.status || "").toUpperCase() ===
                    "POSTED"
              ).length
            }
            caption="Stock déjà alimenté"
            accent="#16a34a"
          />
        </Box>



        <Alert
          severity="info"
          sx={{ mb: 2 }}
        >
          Bon de commande envoyé
          {" → "}
          Réception physique
          {" → "}
          Contrôle
          {" → "}
          Mise en stock
          {" → "}
          Facture fournisseur
          {" → "}
          Paiement.
        </Alert>


        {/* =================================================
            SEARCH
        ================================================= */}

        <Paper
          sx={{
            p: 2,
            mb: 2,
          }}
        >
          <TextField
            fullWidth
            placeholder="Rechercher réception, fournisseur, bon de commande, BL..."
            value={searchTerm}
            onChange={(event) =>
              setSearchTerm(
                event.target.value
              )
            }
          />
        </Paper>


        {/* =================================================
            TABLE
        ================================================= */}

        <Paper
          sx={{
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              overflowX: "auto",
            }}
          >
            <Table
              sx={{
                minWidth: 1100,
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>
                    Réception
                  </TableCell>

                  <TableCell>
                    Bon de commande
                  </TableCell>

                  <TableCell>
                    Fournisseur
                  </TableCell>

                  <TableCell>
                    Entrepôt
                  </TableCell>

                  <TableCell>
                    BL fournisseur
                  </TableCell>

                  <TableCell>
                    Date
                  </TableCell>

                  <TableCell>
                    Articles
                  </TableCell>

                  <TableCell>
                    Statut
                  </TableCell>

                  <TableCell>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      align="center"
                      sx={{ py: 5 }}
                    >
                      <CircularProgress
                        size={26}
                      />
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      sx={{ py: 4 }}
                    >
                      Aucune réception
                      fournisseur.
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(
                    (receipt) => {
                      const normalizedStatus =
                        String(
                          receipt.status ||
                            ""
                        ).toUpperCase()

                      return (
                        <TableRow
                          key={receipt.id}
                          hover
                        >
                          <TableCell>
                            <strong>
                              {receipt.code ||
                                receipt.receipt_number ||
                                `RCV-${receipt.id}`}
                            </strong>
                          </TableCell>

                          <TableCell>
                            {receipt.purchase_order_code ||
                              `#${receipt.purchase_order_id}`}
                          </TableCell>

                          <TableCell>
                            {receipt.supplier_name ||
                              "-"}
                          </TableCell>

                          <TableCell>
                            {receipt.warehouse_code
                              ? `${receipt.warehouse_code} - `
                              : ""}

                            {receipt.warehouse_name ||
                              "-"}
                          </TableCell>

                          <TableCell>
                            {receipt.supplier_delivery_note ||
                              "-"}
                          </TableCell>

                          <TableCell>
                            {receipt.receipt_date
                              ? new Date(
                                  receipt.receipt_date
                                ).toLocaleString(
                                  "fr-FR"
                                )
                              : "-"}
                          </TableCell>

                          <TableCell>
                            {receipt.items_count ??
                              receipt.items
                                ?.length ??
                              "-"}
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              label={statusLabel(
                                receipt.status
                              )}
                              color={statusColor(
                                receipt.status
                              )}
                            />
                          </TableCell>

                          <TableCell>
                            {normalizedStatus ===
                              "RECEIVED" && (
                              <Button
                                variant="contained"
                                size="small"
                                color="success"
                                onClick={() =>
                                  handleControl(
                                    receipt.id
                                  )
                                }
                              >
                                Contrôler
                              </Button>
                            )}

                            {Boolean(
                              receipt.stock_posted
                            ) ||
                            normalizedStatus ===
                              "POSTED" ? (
                              <Chip
                                size="small"
                                color="success"
                                label="Mise en stock ✓"
                              />
                            ) : normalizedStatus ===
                              "CONTROLLED" ? (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Chip
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  label="Prête pour stock"
                                />

                                <Button
                                  variant="contained"
                                  size="small"
                                  color="success"
                                  disabled={
                                    postingReceiptId ===
                                    receipt.id
                                  }
                                  onClick={() =>
                                    handlePostStock(receipt)
                                  }
                                >
                                  {postingReceiptId ===
                                  receipt.id
                                    ? "Mise en stock..."
                                    : "Mettre en stock"}
                                </Button>
                              </Stack>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      )
                    }
                  )
                )}
              </TableBody>
            </Table>
          </Box>
        

          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5]}
            labelRowsPerPage="Lignes"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              "& .MuiTablePagination-toolbar": {
                minHeight: 54,
                px: { xs: 1, sm: 2 },
              },
            }}
          />
        </Paper>
      </Box>


      {/* ===================================================
          DIALOG RÉCEPTION
      =================================================== */}

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
      
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 4 },
            maxHeight: { xs: "100dvh", sm: "90dvh" },
            m: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle>
          Nouvelle réception fournisseur
        </DialogTitle>

        <DialogContent>
          <Stack
            spacing={2.5}
            mt={1}
          >
            {/* =============================================
                PO
            ============================================= */}

            <Stack
              direction={{
                xs: "column",
                md: "row",
              }}
              spacing={2}
            >
              <TextField
                select
                required
                label="Bon de commande"
                value={
                  formData.purchase_order_id
                }
                onChange={(event) =>
                  handleOrderChange(
                    event.target.value
                  )
                }
                disabled={
                  loadingItems || saving
                }
                fullWidth
              >
                {orders.length === 0 && (
                  <MenuItem
                    value=""
                    disabled
                  >
                    Aucun bon à réceptionner
                  </MenuItem>
                )}

                {orders.map(
                  (order) => (
                    <MenuItem
                      key={order.id}
                      value={order.id}
                    >
                      {order.code ||
                        `PO-${order.id}`}

                      {order.supplier_name
                        ? ` • ${order.supplier_name}`
                        : ""}
                    </MenuItem>
                  )
                )}
              </TextField>

              <TextField
                label="Bon de livraison fournisseur"
                value={
                  formData.supplier_delivery_note
                }
                onChange={(event) =>
                  setFormData(
                    (prev) => ({
                      ...prev,

                      supplier_delivery_note:
                        event.target.value,
                    })
                  )
                }
                fullWidth
              />

              <TextField
                type="datetime-local"
                label="Date de réception"
                value={
                  formData.receipt_date
                }
                onChange={(event) =>
                  setFormData(
                    (prev) => ({
                      ...prev,

                      receipt_date:
                        event.target.value,
                    })
                  )
                }
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />
            </Stack>


            {/* =============================================
                PO INFORMATION
            ============================================= */}

            {selectedOrder && (
              <Alert
                severity="success"
                variant="outlined"
              >
                <strong>
                  {
                    selectedOrder.code
                  }
                </strong>

                {" • "}

                {selectedOrder.supplier_name}

                {" • "}

                {selectedOrder.warehouse_code
                  ? `${selectedOrder.warehouse_code} - `
                  : ""}

                {selectedOrder.warehouse_name}
              </Alert>
            )}


            {/* =============================================
                LOADING ITEMS
            ============================================= */}

            {loadingItems && (
              <Stack
                alignItems="center"
                py={4}
              >
                <CircularProgress
                  size={28}
                />

                <Typography
                  variant="body2"
                  mt={1}
                  color="text.secondary"
                >
                  Chargement des articles
                  du bon de commande...
                </Typography>
              </Stack>
            )}


            {/* =============================================
                ITEMS
            ============================================= */}

            {!loadingItems &&
              formData.purchase_order_id &&
              formData.items.length ===
                0 && (
                <Alert severity="warning">
                  Aucun article restant à
                  réceptionner pour ce bon
                  de commande.
                </Alert>
              )}


            {!loadingItems &&
              formData.items.length >
                0 && (
                <>
                  <Divider />

                  <Box>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                    >
                      Produits livrés
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Vérifiez les quantités
                      réellement livrées,
                      acceptées et rejetées
                      avant d'enregistrer.
                    </Typography>
                  </Box>

                  {formData.items.map(
                    (item, index) => {
                      const unit =
                        getUnitLabel(
                          item.unit
                        )

                      return (
                        <Paper
                          key={
                            item.purchase_order_item_id
                          }
                          variant="outlined"
                          sx={{
                            p: 2,
                          }}
                        >
                          <Stack
                            direction={{
                              xs: "column",
                              md: "row",
                            }}
                            justifyContent="space-between"
                            alignItems={{
                              xs: "flex-start",
                              md: "center",
                            }}
                            spacing={1}
                            mb={2}
                          >
                            <Box>
                              <Typography
                                fontWeight="bold"
                                fontSize="1rem"
                              >
                                {item.product_code
                                  ? `${item.product_code} - `
                                  : ""}

                                {
                                  item.product_name
                                }
                              </Typography>

                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Unité :{" "}
                                {unit}
                              </Typography>
                            </Box>

                            <Stack
                              direction={{
                                xs: "column",
                                sm: "row",
                              }}
                              spacing={1}
                            >
                              <Chip
                                size="small"
                                label={`Commandé : ${formatQuantity(
                                  item.ordered_quantity
                                )} ${unit}`}
                              />

                              <Chip
                                size="small"
                                label={`Déjà reçu : ${formatQuantity(
                                  item.already_received_quantity
                                )} ${unit}`}
                                variant="outlined"
                              />

                              <Chip
                                size="small"
                                color="primary"
                                label={`Reste : ${formatQuantity(
                                  item.remaining_quantity
                                )} ${unit}`}
                              />
                            </Stack>
                          </Stack>

                          <Stack
                            direction={{
                              xs: "column",
                              md: "row",
                            }}
                            spacing={2}
                          >
                            <TextField
                              type="number"
                              label={`Livré (${unit})`}
                              value={
                                item.delivered_quantity
                              }
                              onChange={(
                                event
                              ) =>
                                handleDeliveredChange(
                                  index,
                                  event
                                    .target
                                    .value
                                )
                              }
                              inputProps={{
                                min: 0,
                                step: "any",
                              }}
                              fullWidth
                            />

                            <TextField
                              type="number"
                              label={`Accepté (${unit})`}
                              value={
                                item.accepted_quantity
                              }
                              onChange={(
                                event
                              ) =>
                                handleItemChange(
                                  index,
                                  "accepted_quantity",
                                  event
                                    .target
                                    .value
                                )
                              }
                              inputProps={{
                                min: 0,
                                step: "any",
                              }}
                              fullWidth
                            />

                            <TextField
                              type="number"
                              label={`Rejeté (${unit})`}
                              value={
                                item.rejected_quantity
                              }
                              onChange={(
                                event
                              ) =>
                                handleItemChange(
                                  index,
                                  "rejected_quantity",
                                  event
                                    .target
                                    .value
                                )
                              }
                              inputProps={{
                                min: 0,
                                step: "any",
                              }}
                              fullWidth
                            />

                            <TextField
                              label="Motif du rejet"
                              value={
                                item.rejection_reason
                              }
                              onChange={(
                                event
                              ) =>
                                handleItemChange(
                                  index,
                                  "rejection_reason",
                                  event
                                    .target
                                    .value
                                )
                              }
                              disabled={
                                numberValue(
                                  item.rejected_quantity
                                ) <= 0
                              }
                              fullWidth
                            />
                          </Stack>
                        </Paper>
                      )
                    }
                  )}
                </>
              )}


            {/* =============================================
                NOTES
            ============================================= */}

            <TextField
              multiline
              minRows={3}
              label="Observations"
              value={
                formData.notes
              }
              onChange={(event) =>
                setFormData(
                  (prev) => ({
                    ...prev,
                    notes:
                      event.target.value,
                  })
                )
              }
              fullWidth
            />
          </Stack>
        </DialogContent>


        {/* =================================================
            ACTIONS
        ================================================= */}

        <DialogActions
          sx={{
            px: 3,
            pb: 2,
          }}
        >
          <Button
            onClick={handleClose}
            disabled={saving}
          >
            Annuler
          </Button>

          <Button
            variant="contained"
            disabled={
              saving ||
              loadingItems ||
              !formData.purchase_order_id ||
              !formData.items.length
            }
            onClick={handleSave}
          >
            {saving
              ? "Enregistrement..."
              : "Enregistrer réception"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* ===================================================
          SNACKBAR
      =================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnackbar(
            (prev) => ({
              ...prev,
              open: false,
            })
          )
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={
            snackbar.severity
          }
          variant="filled"
          onClose={() =>
            setSnackbar(
              (prev) => ({
                ...prev,
                open: false,
              })
            )
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}


export default PurchaseReceiptsPage